import React, { useState, useEffect } from 'react';
import "../../style/HomeLive/index.scss";
import { useAtom } from "jotai";
import { DarkModeState,showsData } from "../../Jotai/index";
import { NavLink } from "react-router-dom";
import { Link } from "@mui/material";
import { motion } from "framer-motion";
import * as XLSX from "xlsx";

// Decode common HTML entities in external text
const decodeHtml = (s) => {
  if (!s) return "";
  return String(s)
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&#039;|&apos;/g, "'");
};

const HomeLive = (props) => {
  const [darkMode] = useAtom(DarkModeState);
  const [VideoShow] = useAtom(showsData);
    const [currentShow, setCurrentShow] = useState(null);
    const [nextShow, setNextShow] = useState(null);
    const [gcalReady, setGcalReady] = useState(false);
  const [gcalError, setGcalError] = useState(null);
  const isLocalhost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '::1'
  );
  // YouTube Latest video state
  const [ytVideoId, setYtVideoId] = useState("");
  const [ytLoading, setYtLoading] = useState(false);
  const [ytError, setYtError] = useState("");

    const getCurrentPacificTime = () => {
      // Convert current UTC time to Pacific Time
      return new Date(new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));
    };
  // Google Calendar (Online) via API key, with 403 fallback to spreadsheet
    useEffect(() => {
      // Same Online calendar ID as the Schedule embed, decoded from base64 cid
      const ONLINE_CAL_CID_BASE64 = "Y181ZmNiYmFmNmFkNWEyNmNjYWM4MTVjMzFmMDcyODI0YzJmYTEzMWJiNjFlZDY3YWYwMWEyZGQ1YmFkYWYzMTBkQGdyb3VwLmNhbGVuZGFyLmdvb2dsZS5jb20";
      const calendarId = (() => { try { return atob(ONLINE_CAL_CID_BASE64); } catch { return ""; } })();
      // Allow API key via runtime global or env; if missing, we’ll fall back
      const apiKey = process.env.REACT_APP_GCAL_API_KEY;
      if (!calendarId || !apiKey) { setGcalReady(false); setGcalError(!calendarId ? 'Missing calendarId' : 'Missing API key'); return; }

      let abort = false;

      // Fallback: read from "/Online Schedule For Website.xlsx" when API returns 403 (e.g., referrer blocked)
      const fallbackFromSpreadsheet = async () => {
        try {
          const response = await fetch("/Online Schedule For Website.xlsx");
          if (!response.ok) throw new Error(`Spreadsheet fetch failed ${response.status}`);
          const buffer = await response.arrayBuffer();
          const workbook = XLSX.read(buffer, { type: "buffer" });

          const parseDuration = (durationString) => {
            const match = String(durationString || "").match(/^(\d+)\s+Hour/i);
            return match ? parseInt(match[1], 10) : 0;
          };

          const eventsTemp = [];
          const startBase = getCurrentPacificTime();
          for (let offset = 0; offset < 14; offset++) {
            const baseDate = new Date(startBase);
            baseDate.setDate(baseDate.getDate() + offset);
            const dayOfWeek = baseDate.getDay();
            const sheetName = workbook.SheetNames[dayOfWeek];
            const worksheet = workbook.Sheets[sheetName];
            if (!worksheet) continue;
            const data = XLSX.utils.sheet_to_json(worksheet);
            let savePreviousEvent = "";
            data.forEach((row, eventIndex) => {
              if (!row["Duration:"]) return;
              const eventBaseDate = new Date(baseDate);
              eventBaseDate.setHours(eventIndex, 0, 0, 0);

              const durationHours = parseDuration(row["Duration:"]) || 1;
              const endTime = new Date(eventBaseDate.getTime() + durationHours * 60 * 60 * 1000);
              if (savePreviousEvent === row["Show Name:"]) {
                // skip duplicate consecutive slots for the same show
              } else {
                eventsTemp.push({
                  title: row["Show Name:"] || "Untitled",
                  start: eventBaseDate,
                  end: endTime,
                });
              }
              savePreviousEvent = row["Show Name:"];
            });
          }

          if (abort) return;
          const nowPT = getCurrentPacificTime();
          let cur = null, nxt = null;
          for (const ev of eventsTemp.sort((a, b) => a.start - b.start)) {
            if (nowPT >= ev.start && nowPT <= ev.end) cur = ev;
            if (!nxt && ev.start > nowPT) nxt = ev;
            if (cur && nxt) break;
          }
          setCurrentShow(cur);
          setNextShow(nxt);
          setGcalReady(true);
          setGcalError(null);
        } catch (err) {
          if (abort) return;
          console.error("Spreadsheet fallback failed:", err);
          setGcalReady(false);
          setGcalError(err?.message || 'Spreadsheet fallback failed');
        }
      };
      const fetchEvents = async () => {
        try {
          const now = new Date();
          const timeMin = new Date(now.getTime() - 12 * 60 * 60 * 1000).toISOString();
          const timeMax = new Date(now.getTime() + 36 * 60 * 60 * 1000).toISOString();
          const url = new URL(`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events`);
          url.searchParams.set("singleEvents", "true");
          url.searchParams.set("orderBy", "startTime");
          url.searchParams.set("timeMin", timeMin);
          url.searchParams.set("timeMax", timeMax);
          url.searchParams.set("timeZone", "America/Los_Angeles");
          url.searchParams.set("key", apiKey);
          const res = await fetch(url.toString());
          if (!res.ok) {
            const txt = await res.text().catch(() => '');
            if (res.status === 403) {
              console.warn('Google Calendar API 403; falling back to spreadsheet.', txt);
              await fallbackFromSpreadsheet();
              return;
            }
            console.error('Google Calendar API error', res.status, txt);
            throw new Error(`gcal ${res.status} ${txt || ''}`);
          }
          const data = await res.json();
          const items = Array.isArray(data.items) ? data.items : [];
          const evs = items
            .map((it) => {
              const startISO = it.start?.dateTime; // skip all-day (date only)
              const endISO = it.end?.dateTime;
              if (!startISO || !endISO) return null;
              return { title: it.summary || "Untitled", start: new Date(startISO), end: new Date(endISO) };
            })
            .filter(Boolean)
            .sort((a, b) => a.start - b.start);

          if (abort) return;
          const nowPT = getCurrentPacificTime();
          let cur = null, nxt = null;
          for (const ev of evs) {
            if (nowPT >= ev.start && nowPT <= ev.end) cur = ev;
            if (!nxt && ev.start > nowPT) nxt = ev;
            if (cur && nxt) break;
          }
          setCurrentShow(cur);
          setNextShow(nxt);
          setGcalReady(true);
          setGcalError(null);
        } catch (e) {
          setGcalReady(false);
          setGcalError(e?.message || 'Unknown error');
          console.error('GCAL fetch failed:', e);
        }
      };
      fetchEvents();
      const id = setInterval(fetchEvents, 60 * 1000);
      return () => { abort = true; clearInterval(id); };
    }, []);

  // Load latest YouTube video from @22WestTV
  useEffect(() => {
    // On localhost, skip API call and use fallback iframe
    if (isLocalhost) return;
    const API_KEY = process.env.REACT_APP_YT_API_KEY;
    if (!API_KEY) {
      setYtError("Missing YouTube API key");
      return;
    }

    const CACHE_KEY_ID = "yt_latest_video_22westtv_id";
    const CACHE_KEY_TS = "yt_latest_video_22westtv_ts";
    const TTL_MS = 10 * 60 * 1000; // 10 minutes

    const now = Date.now();
    try {
      const cachedId = localStorage.getItem(CACHE_KEY_ID);
      const cachedTs = parseInt(localStorage.getItem(CACHE_KEY_TS) || "0", 10);
      if (cachedId && cachedTs && now - cachedTs < TTL_MS) {
        setYtVideoId(cachedId);
        return;
      }
    } catch {}

    let abort = false;
    const controller = new AbortController();
    const signal = controller.signal;
    setYtLoading(true);
    setYtError("");

    const YT_BASE = "https://www.googleapis.com/youtube/v3";
    const handle = "@22WestTV";

    const fetchJson = async (url) => {
      const res = await fetch(url, { signal });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`${res.status} ${txt}`);
      }
      return res.json();
    };

    const resolveChannelId = async () => {
      // Try forHandle first (supported in YouTube Data API v3)
      try {
        const u1 = `${YT_BASE}/channels?part=id&forHandle=${encodeURIComponent(handle)}&key=${API_KEY}`;
        const j1 = await fetchJson(u1);
        const id = j1?.items?.[0]?.id;
        if (id) return id;
      } catch {}
      // Fallback: search for the channel by name
      const u2 = `${YT_BASE}/search?part=snippet&type=channel&maxResults=1&q=${encodeURIComponent("22WestTV")}&key=${API_KEY}`;
      const j2 = await fetchJson(u2);
      return j2?.items?.[0]?.id?.channelId || "";
    };

    const run = async () => {
      try {
        const channelId = await resolveChannelId();
        if (!channelId) throw new Error("Channel not found");
        // Get latest uploaded video
        const u3 = `${YT_BASE}/search?part=snippet&channelId=${encodeURIComponent(channelId)}&maxResults=1&order=date&type=video&key=${API_KEY}`;
        const j3 = await fetchJson(u3);
        const vid = j3?.items?.[0]?.id?.videoId || "";
        if (!vid) throw new Error("No videos found");
        if (abort) return;
        setYtVideoId(vid);
        try {
          localStorage.setItem(CACHE_KEY_ID, vid);
          localStorage.setItem(CACHE_KEY_TS, String(Date.now()));
        } catch {}
      } catch (e) {
        if (abort) return;
        setYtError(e?.message || "Failed to load YouTube video");
      } finally {
        if (!abort) setYtLoading(false);
      }
    };

    run();
    return () => { abort = true; controller.abort(); };
  }, [isLocalhost]);

  const legacyIframe = (Array.isArray(VideoShow) && VideoShow[0]?.show) || "";
  const embedSrc = isLocalhost
    ? legacyIframe
    : (ytVideoId
        ? `https://www.youtube.com/embed/${ytVideoId}?rel=0&modestbranding=1&color=white`
        : legacyIframe);


  return (
    <>
      <div className="HomeLive">
        <div className="HomeLiveCard">
          <motion.div className={darkMode?"cardSection":"cardSection w_bg_color"} whileInView={{ scale: 1 ,transition: { duration: 0.6 } }} initial={{ scale: 0 }}>
            <div className="liveRadioTitle">
              <div className="liveRadioTitleRedbtn"></div>
              <div className="liveRadioTitleTxt">Live on Radio</div>
            </div>
            <div className={darkMode?"radioPlaylistNameactive":"radioPlaylistNameactive"}>
                {/* <div className="radioPlaylistName_nowplaying">Now Playing:</div> */}
                <div className="radioPlaylistName_name">{currentShow ? decodeHtml(currentShow.title) : "No Shows right now"}</div>
                {/* <div className="radioPlaylistName_txt">{currentSong.artist!==""?currentSong.artist:""}</div> */}
                <div className="radioPlaylistName_time">{currentShow?currentShow.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' }):""} - {currentShow?currentShow.end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' }):''}</div>
                {!gcalReady && gcalError && (
                  <div style={{ color: '#f87171', fontSize: 12, marginTop: 4 }}>Calendar error: {gcalError}</div>
                )}
            </div>

            <div className="upNextTitle">Up Next...</div>
            <div className={darkMode?"radioPlaylistName":"radioPlaylistName"}>
                <div className="radioPlaylistName_name">{nextShow ? decodeHtml(nextShow.title) : "No show up next"}</div>
                {/* <div className="radioPlaylistName_txt"></div> */}
                <div className="radioPlaylistName_time">{nextShow ? (nextShow.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' }) + " - " + nextShow.end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' })) : "No show up next"}</div>
            </div>
          </motion.div>
          <motion.div className={darkMode?"cardSection":"cardSection w_bg_color"}  whileInView={{ scale: 1 ,transition: { duration: 0.6 } }} initial={{ scale: 0 }}>
            <div className="newVideoSection">Latest in TV</div>
            <div className="videoIframe">
              {embedSrc ? (
                <iframe
                  width="100%"
                  height="200px"
                  src={embedSrc}
                  title="Latest YouTube video"
                  frameBorder={0}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <div style={{ fontSize: 14, color: '#999', padding: '8px 0' }}>
                  {ytLoading ? 'Loading latest video…' : (ytError ? 'Could not load latest video.' : 'No video available.')}
                </div>
              )}
            </div>
            {!isLocalhost && ytError && (
              <div style={{ color: '#f87171', fontSize: 12, marginTop: 6 }}>{ytError}</div>
            )}
          </motion.div>
          <motion.div className={darkMode?"cardSection":"cardSection w_bg_color"}  whileInView={{ scale: 1,transition: { duration: 0.6 }  }} initial={{ scale: 0 }}>
            <div className="NewMagazine">Latest Mag Issue</div>
            <NavLink to={"https://issuu.com/22westmagazine"} className={"linkinmagazineSearch"} target="_blank" bis_size={"none"}>
            <img src="image/music_issue.jpg" className="NewMagazineimg" alt="The Music Issue" bis_size={"none"} style={{ width: "100%", height: "200px", objectFit: "cover", objectPosition: "top" }} />
            </NavLink>
            <div></div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default HomeLive;
